import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useAppDispatch, useAppSelector } from '@/store';
import { setSessionExpired, clearSessionExpired } from '@/store/slices/authSlice';
import { SESSION_EXPIRED_EVENT } from '@/auth/sessionEvents';
import type { SessionExpiredDetail } from '@/auth/sessionEvents';
import { sfAuth } from '@/auth/SFAuth';

const RETURN_TO_KEY = 'auth:returnTo';

export const SessionExpiredDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const { sessionExpired, sessionExpiredReason } = useAppSelector((state) => state.auth);
  const [isLoading, setIsLoading] = useState(false);

  // Listen for session expired events from the API layer
  useEffect(() => {
    const handleSessionExpired = (event: Event) => {
      const customEvent = event as CustomEvent<SessionExpiredDetail>;
      dispatch(setSessionExpired({ expired: true, reason: customEvent.detail.reason }));
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    };
  }, [dispatch]);

  // Clear session expired state when component unmounts (e.g., on page refresh)
  useEffect(() => {
    return () => {
      dispatch(clearSessionExpired());
    };
  }, [dispatch]);

  const handleSignIn = () => {
    setIsLoading(true);
    // Save current location for return after OAuth
    sessionStorage.setItem(RETURN_TO_KEY, window.location.pathname + window.location.search);
    sfAuth.startAuthFlow();
  };

  return (
    <Dialog
      open={sessionExpired}
      maxWidth="xs"
      fullWidth
      aria-labelledby="session-expired-dialog-title"
      aria-describedby="session-expired-dialog-description"
      // Prevent closing by clicking outside or pressing escape
      disableEscapeKeyDown
    >
      <DialogTitle id="session-expired-dialog-title">
        <Box display="flex" alignItems="center" gap={1}>
          <WarningAmberIcon color="warning" />
          <Typography variant="h6">Session Expired</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography id="session-expired-dialog-description" variant="body1" sx={{ mt: 1 }}>
          {sessionExpiredReason || 'Your session has expired after being idle. Click below to sign in again.'}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSignIn}
          disabled={isLoading}
          autoFocus
          fullWidth
        >
          {isLoading ? (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={20} color="inherit" />
              Redirecting...
            </Box>
          ) : (
            'Sign In Again'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
